import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@bw/shared/hooks";
import { useEffect, useMemo, useState } from "react";
import { Page } from "../../../common/components/Page";
import {
  gamificationApi,
  type GamificationAchievement,
  type GamificationSelection,
} from "../api";

function achievementTone(status: GamificationAchievement["status"]) {
  if (status === "earned") return "success" as const;
  if (status === "revoked") return "warning" as const;
  return "default" as const;
}

function achievementStatusLabel(status: GamificationAchievement["status"]) {
  if (status === "revoked") return "Corrected";
  return status[0].toUpperCase() + status.slice(1);
}

export function GamificationPage() {
  const { user } = useAuth();
  const cache = useQueryClient();
  const summaryKey = ["gamification", "mine", user?.uid ?? null] as const;
  const summary = useQuery({
    queryKey: summaryKey,
    queryFn: gamificationApi.mine,
    enabled: Boolean(user),
    retry: false,
  });
  const [draft, setDraft] = useState<Pick<
    GamificationSelection,
    | "profile_accent"
    | "avatar_ring"
    | "featured_achievement_key"
    | "show_streak_publicly"
  > | null>(null);

  useEffect(() => {
    if (summary.data) {
      const { selection } = summary.data;
      setDraft({
        profile_accent: selection.profile_accent,
        avatar_ring: selection.avatar_ring,
        featured_achievement_key: selection.featured_achievement_key,
        show_streak_publicly: selection.show_streak_publicly,
      });
    }
  }, [summary.data]);

  const refresh = () => cache.invalidateQueries({ queryKey: summaryKey });
  const selectCosmetics = useMutation({
    mutationFn: () =>
      gamificationApi.selectCosmetics(summary.data!.selection, draft!),
    onSuccess: refresh,
  });
  const visibility = useMutation({
    mutationFn: ({
      achievement,
      next,
    }: {
      achievement: GamificationAchievement;
      next: GamificationAchievement["visibility"];
    }) => gamificationApi.setAchievementVisibility(achievement, next),
    onSuccess: refresh,
  });

  const earned = useMemo(
    () =>
      summary.data?.achievements.filter(
        (item) => item.status === "earned" && item.visibility === "public",
      ) ?? [],
    [summary.data],
  );
  const selection = summary.data?.selection;
  const dirty = Boolean(
    draft &&
      selection &&
      (draft.profile_accent !== selection.profile_accent ||
        draft.avatar_ring !== selection.avatar_ring ||
        draft.featured_achievement_key !== selection.featured_achievement_key ||
        draft.show_streak_publicly !== selection.show_streak_publicly),
  );

  return (
    <Page title="Achievements & streaks">
      <Stack spacing={3}>
        <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
          Built from verified BW activity—not self-reported play or spending.
          Competitive rankings remain separate.
        </Typography>
        {summary.isLoading ? (
          <Typography role="status">Loading verified progress…</Typography>
        ) : summary.isError || !summary.data ? (
          <Alert severity="error">
            We couldn&apos;t load verified progress. Your ranking and bookings
            are unaffected.
          </Alert>
        ) : (
          <>
            <Card
              component="section"
              aria-labelledby="weekly-streak-title"
              variant="outlined"
              sx={{
                background: (theme) =>
                  `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              }}
            >
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{
                    alignItems: { sm: "center" },
                    justifyContent: "space-between",
                  }}
                >
                  <Stack spacing={0.5}>
                    <Typography
                      id="weekly-streak-title"
                      component="h2"
                      variant="h5"
                    >
                      Weekly play streak
                    </Typography>
                    <Typography color="text.secondary">
                      One verified play keeps a week active. No daily pressure.
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={3}
                    sx={{ alignItems: "baseline" }}
                  >
                    <Box>
                      <Typography
                        variant="h3"
                        sx={{ fontWeight: 750, lineHeight: 1 }}
                      >
                        {summary.data.streak.current_weeks}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        current weeks
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {summary.data.streak.longest_weeks}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        longest
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{ mt: 2, flexWrap: "wrap" }}
                >
                  <Chip
                    color={
                      summary.data.streak.this_week_complete
                        ? "success"
                        : "default"
                    }
                    label={
                      summary.data.streak.this_week_complete
                        ? "This week complete"
                        : "Play once this week to continue"
                    }
                  />
                  <Chip
                    variant="outlined"
                    label={`${summary.data.streak.verified_play_count} verified plays`}
                  />
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1.5 }}
                >
                  Weeks use {summary.data.streak.timezone}. Corrections can
                  update progress without hiding the original evidence.
                </Typography>
              </CardContent>
            </Card>

            <Stack
              component="section"
              spacing={1.5}
              aria-labelledby="achievement-title"
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={0.5}
                sx={{
                  alignItems: { xs: "flex-start", sm: "baseline" },
                  justifyContent: "space-between",
                }}
              >
                <Typography id="achievement-title" component="h2" variant="h5">
                  Achievements
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Private until you choose otherwise
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                {summary.data.achievements.map((achievement) => {
                  const percent = Math.min(
                    100,
                    (achievement.progress / achievement.target) * 100,
                  );
                  return (
                    <Grid key={achievement.key} size={{ xs: 12, sm: 6 }}>
                      <Card variant="outlined" sx={{ height: "100%" }}>
                        <CardContent>
                          <Stack spacing={1.25}>
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography component="h3" variant="h6">
                                {achievement.title}
                              </Typography>
                              <Chip
                                size="small"
                                color={achievementTone(achievement.status)}
                                label={achievementStatusLabel(
                                  achievement.status,
                                )}
                              />
                            </Stack>
                            <Typography color="text.secondary" variant="body2">
                              {achievement.description}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={percent}
                              aria-label={`${achievement.title} progress`}
                            />
                            <Stack
                              direction="row"
                              sx={{
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {achievement.progress} / {achievement.target}
                              </Typography>
                              {achievement.status === "earned" ? (
                                <Button
                                  size="small"
                                  disabled={visibility.isPending}
                                  onClick={() =>
                                    visibility.mutate({
                                      achievement,
                                      next:
                                        achievement.visibility === "public"
                                          ? "private"
                                          : "public",
                                    })
                                  }
                                >
                                  {achievement.visibility === "public"
                                    ? "Make private"
                                    : "Show on profile"}
                                </Button>
                              ) : null}
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              {visibility.isError ? (
                <Alert severity="error">
                  We couldn&apos;t change that achievement&apos;s profile
                  visibility. Refresh and try again.
                </Alert>
              ) : null}
            </Stack>

            {draft ? (
              <Card
                component="section"
                aria-labelledby="cosmetics-title"
                variant="outlined"
              >
                <CardContent>
                  <Stack spacing={2}>
                    <Stack spacing={0.5}>
                      <Typography
                        id="cosmetics-title"
                        component="h2"
                        variant="h5"
                      >
                        Profile style
                      </Typography>
                      <Typography color="text.secondary">
                        Choose among earned, subtle treatments. These never
                        affect ranking.
                      </Typography>
                    </Stack>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                      <FormControl fullWidth>
                        <InputLabel id="profile-accent-label">
                          Profile accent
                        </InputLabel>
                        <Select
                          labelId="profile-accent-label"
                          label="Profile accent"
                          value={draft.profile_accent}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              profile_accent: event.target.value,
                            })
                          }
                        >
                          {summary.data.cosmetics
                            .filter((item) => item.kind === "profile_accent")
                            .map((item) => (
                              <MenuItem
                                key={item.key}
                                value={item.key}
                                disabled={!item.owned}
                              >
                                {item.label}
                                {item.owned ? "" : " · locked"}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth>
                        <InputLabel id="avatar-ring-label">
                          Avatar ring
                        </InputLabel>
                        <Select
                          labelId="avatar-ring-label"
                          label="Avatar ring"
                          value={draft.avatar_ring}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              avatar_ring: event.target.value,
                            })
                          }
                        >
                          {summary.data.cosmetics
                            .filter((item) => item.kind === "avatar_ring")
                            .map((item) => (
                              <MenuItem
                                key={item.key}
                                value={item.key}
                                disabled={!item.owned}
                              >
                                {item.label}
                                {item.owned ? "" : " · locked"}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth>
                        <InputLabel id="featured-achievement-label">
                          Featured badge
                        </InputLabel>
                        <Select
                          labelId="featured-achievement-label"
                          label="Featured badge"
                          value={draft.featured_achievement_key ?? ""}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              featured_achievement_key:
                                event.target.value || null,
                            })
                          }
                        >
                          <MenuItem value="">None</MenuItem>
                          {earned.map((item) => (
                            <MenuItem key={item.key} value={item.key}>
                              {item.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={draft.show_streak_publicly}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              show_streak_publicly: event.target.checked,
                            })
                          }
                        />
                      }
                      label="Show my current weekly streak on my public profile"
                    />
                    {selectCosmetics.isError ? (
                      <Alert severity="error">
                        Your selection changed. Refresh and try again.
                      </Alert>
                    ) : null}
                    {selectCosmetics.isSuccess && !dirty ? (
                      <Alert severity="success">Profile style saved.</Alert>
                    ) : null}
                    <Button
                      variant="contained"
                      disabled={selectCosmetics.isPending || !dirty}
                      onClick={() => selectCosmetics.mutate()}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      Save profile style
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ) : null}
          </>
        )}
      </Stack>
    </Page>
  );
}
